import { useCallback, useEffect, useState } from 'react';
import { EmblaCarouselType } from 'embla-carousel';
import { tv } from 'tailwind-variants';

type UseDotButtonType = {
  selectedIndex: number;
  scrollSnaps: number[];
  onDotButtonClick: (index: number) => void;
};

const dotButton = tv({
  slots: {
    container: ['flex items-center justify-center no-underline cursor-pointer border-0 p-0 m-0 w-2 h-2 rounded-full'],
  },

  variants: {
    color: {
      gray: {},
      green: {},
      success: {},
    },

    selected: {
      true: {
        container: [],
      },
      false: {
        container: [],
      },
    },
  },

  compoundVariants: [
    {
      color: 'gray',
      selected: true,
      class: {
        container: 'bg-gray-500',
      },
    },
    {
      color: 'gray',
      selected: false,
      class: {
        container: 'bg-gray-300',
      },
    },
    {
      color: 'green',
      selected: true,
      class: {
        container: 'bg-green-100',
      },
    },
    {
      color: 'green',
      selected: false,
      class: {
        container: 'bg-green-200',
      },
    },
    {
      color: 'success',
      selected: true,
      class: {
        container: 'bg-success-400',
      },

    },
    {
      color: 'success',
      selected: false,
      class: {
        container: 'bg-white',
      },
    },
  ],

  defaultVariants: {
    color: 'gray',
  },
});

export const useDotButton = (emblaApi: EmblaCarouselType | undefined): UseDotButtonType => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi) {
        return;
      }
      emblaApi.scrollTo(index);
    },
    [emblaApi],
  );

  const onInit = useCallback(
    (emblaApi: EmblaCarouselType) => {
      if (!emblaApi) {
        return;
      }
      setScrollSnaps(emblaApi.scrollSnapList());
    },
    [setScrollSnaps],
  );

  const onSelect = useCallback(
    (emblaApi: EmblaCarouselType) => {
      if (!emblaApi) {
        return;
      }
      setSelectedIndex(emblaApi.selectedScrollSnap());
    },
    [setSelectedIndex],
  );

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on('reInit', onInit);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('select', onSelect);
  }, [emblaApi, onInit, onSelect]);

  return {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick,
  };
};

export enum DotsVariantEnum {
  GRAY = 'gray',
  GREEN = 'green',
  SUCCESS = 'success',
}

type DotButtonProps = React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> & {
  selected: boolean;
  variant?: DotsVariantEnum;
};

export const DotButton: React.FC<DotButtonProps> = ({ selected, variant, children, ...restProps }) => {
  const className = dotButton({
    selected: selected,
    color: variant || DotsVariantEnum.GRAY,
  }).container();

  return (
    <button type="button" className={className} {...restProps}>
      {children}
    </button>
  );
};
